import { useState, type FormEvent } from 'react';
import { useLocation } from 'wouter';
import { useSession } from '@/auth/SessionContext';
import { CustomerAuthError } from '@/api/customerSession';
import { Card, PrimaryButton } from '@/components/AppShell';

/**
 * 客户登录页（Issue #198）。
 *
 * 红线：
 *   - 表单输入 trim 后才发送；空字段不发请求。
 *   - 失败提示按 CustomerAuthError.kind 分级，不暴露原始 HTTP 状态。
 *   - 提交期间禁用按钮并显示 loading 文案，避免重复提交。
 *   - 永远不在 console 或 DOM 中打印密码字段。
 */
export function LoginPage() {
  const { signIn } = useSession();
  const [, navigate] = useLocation();
  const [orgSlug, setOrgSlug] = useState('');
  const [orgId, setOrgId] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setErrorMessage(null);
    setSubmitting(true);
    try {
      await signIn({
        orgSlug,
        orgId,
        tenantId,
        phone,
        password,
      });
      navigate('/');
    } catch (err) {
      if (err instanceof CustomerAuthError) {
        setErrorMessage(err.message);
      } else {
        setErrorMessage('登录失败，请稍后重试');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-[480px] flex-col bg-background px-4 py-8 safe-top safe-bottom">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold">金专细胞健康</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          客户中心 · 查看服务进度、上传资料、查看已审核报告
        </p>
      </header>
      <Card className="space-y-4">
        <form className="space-y-4" onSubmit={handleSubmit} noValidate>
          <Field
            label="组织标识 orgSlug"
            value={orgSlug}
            onChange={setOrgSlug}
            autoComplete="organization"
            placeholder="例如 demo-clinic"
            required
          />
          <Field
            label="组织 ID"
            value={orgId}
            onChange={setOrgId}
            placeholder="由顾问提供"
            required
          />
          <Field
            label="租户 ID"
            value={tenantId}
            onChange={setTenantId}
            placeholder="第一版与组织 ID 相同"
            required
          />
          <Field
            label="手机号"
            value={phone}
            onChange={setPhone}
            autoComplete="tel"
            inputMode="tel"
            placeholder="11 位手机号"
            required
          />
          <Field
            label="密码"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            type="password"
            placeholder="登录密码"
            required
          />
          {errorMessage ? (
            <p role="alert" className="text-sm text-destructive">
              {errorMessage}
            </p>
          ) : null}
          <PrimaryButton type="submit" disabled={submitting} full>
            {submitting ? '登录中…' : '登录'}
          </PrimaryButton>
        </form>
        <p className="text-xs text-muted-foreground">
          本端口为客户专属，员工请使用管理后台登录。账号疑问请联系您的专属顾问，
          切勿在公共设备保存密码。
        </p>
      </Card>
    </div>
  );
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  autoComplete?: string;
  inputMode?: 'tel' | 'text' | 'email' | 'numeric';
  required?: boolean;
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  autoComplete,
  inputMode,
  required,
}: FieldProps) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <input
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        type={type}
        autoComplete={autoComplete}
        inputMode={inputMode}
        required={required}
      />
    </label>
  );
}
